import React, {useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Link} from 'react-router-dom';
import {PROJECT_INSTRUCTION} from '../../../utils/promptAskPhrase';
import {ArrowRight, Check, Copy} from '../../icons';

const INSTRUCTION_ID = 'agp-setup-instruction';

function StepMark({done, number}) {
    return (
        <span className={`agp-setup__mark${done ? ' -done' : ''}`} aria-hidden="true">
            {done ? <Check size={13} weight="bold"/> : number}
        </span>
    );
}

/**
 * «Prepara tu IA»: lo que cada persona hace UNA vez para pedir prompts por su nombre. Reemplaza a
 * «Cómo pedirlos desde tu IA», que mezclaba esto con el uso de todos los días y le daba al texto de la
 * instrucción —que se pega una sola vez— el bloque más pesado de la pantalla.
 *
 * Dos pasos con estado. El 1 (conectar la IA) lo marca el backend si viene `connectedByBackend` (la IA
 * de ESTE usuario consultó Dashbo hace poco); si no, la persona lo marca a mano. El 2 (pegar la
 * instrucción en el Proyecto) no lo puede saber nadie más que ella: siempre a mano. Con los dos
 * hechos, la tarjeta se achica a «Tu IA está lista» y se vuelve a abrir con «Ver la configuración».
 */
export function SetupAiCard({
    connectedByBackend, aiConnected, instructionPasted, onConnectedChange, onPastedChange, onCopyInstruction,
}) {
    const {t} = useTranslation();
    const [expanded, setExpanded] = useState(false);
    const [showText, setShowText] = useState(false);
    const connected = Boolean(connectedByBackend || aiConnected);
    const doneCount = (connected ? 1 : 0) + (instructionPasted ? 1 : 0);
    const ready = doneCount === 2;

    if (ready && !expanded) {
        return (
            <aside className="agp-setup -ready" aria-labelledby="agp-setup-title">
                <div className="agp-setup__ready">
                    <StepMark done/>
                    <div>
                        <h2 id="agp-setup-title" className="agp-setup__title">{t('agency_prompts.setup_ready_title')}</h2>
                        <p className="agp-setup__desc">{t('agency_prompts.setup_ready_body')}</p>
                    </div>
                </div>
                <button type="button" className="agp-btn -link agp-setup__reopen" onClick={() => setExpanded(true)}>
                    {t('agency_prompts.setup_reopen')}
                </button>
            </aside>
        );
    }

    return (
        <aside className="agp-setup" aria-labelledby="agp-setup-title">
            <div className="agp-setup__head">
                <h2 id="agp-setup-title" className="agp-setup__title">{t('agency_prompts.setup_title')}</h2>
                <span className={`agp-setup__progress${ready ? ' -done' : ''}`}>
                    {t('agency_prompts.setup_progress', {done: doneCount})}
                </span>
            </div>
            <p className="agp-setup__lead">{t('agency_prompts.setup_lead')}</p>
            <ol className="agp-setup__steps">
                <li className="agp-setup__step">
                    <StepMark done={connected} number={1}/>
                    <div className="agp-setup__step-main">
                        <span className="agp-setup__step-title">{t('agency_prompts.setup_step1_title')}</span>
                        {connectedByBackend ? (
                            <span className="agp-setup__desc -done">{t('agency_prompts.setup_step1_detected')}</span>
                        ) : (
                            <>
                                <span className="agp-setup__desc">{t('agency_prompts.setup_step1_body')}</span>
                                {!aiConnected && (
                                    <Link className="agp-btn -ghost agp-setup__cta" to="/dashbo-mcp">
                                        {t('agency_prompts.setup_step1_link')}<ArrowRight size={14} aria-hidden="true"/>
                                    </Link>
                                )}
                                <label className="agp-setup__check">
                                    <input type="checkbox" checked={Boolean(aiConnected)}
                                           onChange={(event) => onConnectedChange(event.target.checked)}/>
                                    {t('agency_prompts.setup_step1_check')}
                                </label>
                            </>
                        )}
                    </div>
                </li>
                <li className="agp-setup__step">
                    <StepMark done={Boolean(instructionPasted)} number={2}/>
                    <div className="agp-setup__step-main">
                        <span className="agp-setup__step-title">{t('agency_prompts.setup_step2_title')}</span>
                        <span className="agp-setup__desc">{t('agency_prompts.setup_step2_body')}</span>
                        <div className="agp-setup__actions">
                            <button type="button" className="agp-btn -quiet" onClick={onCopyInstruction}>
                                <Copy size={14} aria-hidden="true"/>{t('agency_prompts.howto_copy_instruction')}
                            </button>
                            <button type="button" className="agp-btn -link" aria-expanded={showText}
                                    aria-controls={INSTRUCTION_ID} onClick={() => setShowText((value) => !value)}>
                                {showText ? t('agency_prompts.setup_hide_text') : t('agency_prompts.setup_show_text')}
                            </button>
                        </div>
                        {showText && <pre id={INSTRUCTION_ID} className="agp-setup__snippet">{PROJECT_INSTRUCTION}</pre>}
                        <label className="agp-setup__check">
                            <input type="checkbox" checked={Boolean(instructionPasted)}
                                   onChange={(event) => onPastedChange(event.target.checked)}/>
                            {t('agency_prompts.setup_step2_check')}
                        </label>
                    </div>
                </li>
            </ol>
            <p className="agp-setup__note">{t('agency_prompts.howto_note')}</p>
        </aside>
    );
}
